import { FormDataFlattener } from './FormDataFlattener.js';

/**
 * Utility for building form elements with hidden inputs for submission
 * Handles method spoofing, CSRF tokens, and Turbo Frame configuration
 */
export class HiddenFormBuilder {
  /**
   * Creates a form element with all necessary configuration
   * @param {Object} options - Form configuration options
   * @param {string} options.url - The form action URL
   * @param {string} options.method - HTTP method (default: 'POST')
   * @param {string} options.turboFrame - Turbo Frame ID (optional)
   * @param {Object} options.formData - Data to include in the form
   * @returns {HTMLFormElement} Configured form element
   */
  static createForm(options) {
    const {
      url,
      method = 'POST',
      turboFrame,
      formData = {}
    } = options;

    // Create the form element
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.style.display = 'none';

    // Add turbo frame attribute if specified
    if (turboFrame) {
      form.setAttribute('data-turbo-frame', turboFrame);
    }

    // Add method spoofing if needed
    if (method && method.toUpperCase() !== 'POST') {
      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = method.toUpperCase();
      form.appendChild(methodInput);
    }

    // Add CSRF token automatically
    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add form data
    this.addFormData(form, formData);

    return form;
  }

  /**
   * Adds form data to a form element as hidden inputs
   * @param {HTMLFormElement} form - The form element
   * @param {Object} formData - The data to add
   */
  static addFormData(form, formData) {
    const flattenedData = FormDataFlattener.flatten(formData);
    flattenedData.forEach(({ key, value }) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });
  }

  /**
   * Creates and submits a form with the given configuration
   * @param {Object} options - Form configuration options
   * @param {string} options.url - The form action URL
   * @param {string} options.method - HTTP method (default: 'POST')
   * @param {string} options.turboFrame - Turbo Frame ID (optional)
   * @param {Object} options.formData - Data to include in the form
   */
  static async submitForm(options) {
    const form = this.createForm(options);

    // Add form to DOM, submit, and remove
    document.body.appendChild(form);
    form.requestSubmit();
    document.body.removeChild(form);
  }

  /**
   * Gets CSRF token from the page
   * @returns {string|null} CSRF token or null if not found
   */
  static getCsrfToken() {
    return document.querySelector('[name="csrf-token"]')?.content || null;
  }
}
